/**
 * AI 科学圆桌调试控制台
 *   - 默认关闭，零成本
 *   - 开启：localStorage.expoDebug = "1" 或 Ctrl+Shift+D
 *   - 显示分类标签 + 时间戳 + 颜色级别
 *   - 可自由拖拽定位
 */
import React, { useCallback, useEffect, useRef, useState } from "react";
import debugLog from "../../../utils/debugLog.js";
import latencyTrace from "../../../utils/latencyTrace.js";

const CATEGORY_COLORS = {
  PRELOAD:  "#7dd3fc",
  SESSION:  "#a78bfa",
  WS:       "#22d3ee",
  SPEAKER:  "#fbbf24",
  TTS:      "#f472b6",
  ASR:      "#34d399",
  RENDER:   "#fb923c",
  STATE:    "#94a3b8",
  QUEUE:    "#c4b5fd",
  PRESPEAK: "#5eead4",
  PERF:     "#f87171",
};

const writeClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (_) {
    const ta = document.createElement("textarea");
    ta.value = text;
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand("copy"); } catch (__) {}
    ta.remove();
  }
};

// 初始位置：左下角
const INITIAL_POS = { x: 16, y: null }; // y=null 表示用 bottom:16px

const ExpoDebugConsole = () => {
  const [enabled, setEnabled] = useState(debugLog.isEnabled());
  const [entries, setEntries] = useState(() => debugLog.snapshot());
  const [paused, setPaused] = useState(false);
  // pos: { x, y } 均为距视口左/上的 px；初始 null 时走 CSS bottom:16px
  const [pos, setPos] = useState(INITIAL_POS);
  const bodyRef = useRef(null);
  const consoleRef = useRef(null);
  const dragState = useRef(null); // { startMouseX, startMouseY, startElX, startElY }

  useEffect(() => {
    return debugLog.subscribe((_entry, all) => {
      setEnabled(debugLog.isEnabled());
      setEntries([...all]);
    });
  }, []);

  // 自动滚到底
  useEffect(() => {
    if (!paused && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [entries, paused]);

  // ── 拖拽逻辑 ──────────────────────────────────────────────────────────────
  const onHeaderMouseDown = useCallback((e) => {
    // 只响应左键；点按钮不触发拖拽
    if (e.button !== 0 || e.target.tagName === "BUTTON") return;
    e.preventDefault();
    e.stopPropagation();

    const el = consoleRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    dragState.current = {
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startElX: rect.left,
      startElY: rect.top,
    };

    const onMouseMove = (me) => {
      if (!dragState.current) return;
      const { startMouseX, startMouseY, startElX, startElY } = dragState.current;
      const dx = me.clientX - startMouseX;
      const dy = me.clientY - startMouseY;
      const newX = Math.max(0, Math.min(window.innerWidth  - rect.width,  startElX + dx));
      const newY = Math.max(0, Math.min(window.innerHeight - rect.height, startElY + dy));
      setPos({ x: newX, y: newY });
    };

    const onMouseUp = () => {
      dragState.current = null;
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup",   onMouseUp);
    };

    window.addEventListener("mousemove", onMouseMove);
    window.addEventListener("mouseup",   onMouseUp);
  }, []);
  // ─────────────────────────────────────────────────────────────────────────

  if (!enabled) return null;

  const copyAll = async () => {
    const text = entries.map((e) => `${e.tsLabel} [${e.category}] ${e.msg}`).join("\n");
    await writeClipboard(text);
    console.log(`[debugLog] 已复制 ${entries.length} 条到剪贴板`);
  };

  const copyPerfReport = async () => {
    const report = latencyTrace.report();
    await writeClipboard(`${report}\n\n--- 明细 CSV ---\n${latencyTrace.csv()}`);
    console.log(`[latencyTrace] 耗时报告已复制\n${report}`);
  };

  // 计算内联 style：有 y 坐标时用 top，否则用 bottom:16px
  const posStyle = pos.y !== null
    ? { left: pos.x, top: pos.y, bottom: "auto" }
    : { left: pos.x, bottom: 16,  top:  "auto"  };

  return (
    <div
      ref={consoleRef}
      className="expo-debug-console"
      style={posStyle}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {/* header 区域负责拖拽，阻止 mousedown 冒泡到舞台拖拽逻辑 */}
      <div
        className="expo-debug-header expo-debug-draggable"
        onMouseDown={onHeaderMouseDown}
      >
        <span className="expo-debug-title">📡 调试日志</span>
        <span className="expo-debug-meta">
          {entries.length} 条 · {paused ? "已暂停" : "实时"}
        </span>
        <button className="expo-debug-btn" onClick={copyAll} title="复制全部到剪贴板">复制全部</button>
        <button
          className="expo-debug-btn"
          onClick={copyPerfReport}
          title="复制分段耗时统计与明细 CSV"
        >耗时报告</button>
        <button
          className="expo-debug-btn"
          onClick={() => { debugLog.clear(); latencyTrace.clear(); }}
          title="清空日志与耗时样本"
        >清空</button>
        <button
          className="expo-debug-btn"
          onClick={() => debugLog.setEnabled(false)}
          title="关闭面板（Ctrl+Shift+D 重新打开）"
        >关闭</button>
      </div>
      <div className="expo-debug-body" ref={bodyRef}>
        {entries.length === 0 ? (
          <div className="expo-debug-empty">尚无日志…</div>
        ) : entries.map((e, i) => (
          <div key={i} className={`expo-debug-row level-${e.level}`}>
            <span className="expo-debug-ts">{e.tsLabel}</span>
            <span
              className="expo-debug-cat"
              style={{ color: CATEGORY_COLORS[e.category] || "#cbd5e1" }}
            >{e.category}</span>
            <span className="expo-debug-msg">{e.msg}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExpoDebugConsole;
